import { last } from "@prismicio/editor-support/Array"
import {
  Block,
  EmbedBlock,
  ImageBlock,
  RichTextContent,
  TextBlock
} from "@prismicio/types-internal/lib/content"
import TTDocument from "@tiptap/extension-document"
import type { Node as ProsemirrorNode, Schema } from "prosemirror-model"

import { BulletList, OrderedList } from "../extensions"
import { DocumentExtension, type NodeExtension, NodeType } from "../models"
import { NodeUtils } from "../models/helpers/NodeUtils"
import { mergeLists } from "./ListItem"

const componentName = "document"
const component = (multiLines: boolean) =>
  TTDocument.extend({
    name: componentName,
    content: multiLines ? "block+" : "block"
  })

const converter = (extensions: NodeExtension[]) => {
  return {
    fromPrismic(
      schema: Schema,
      defaultTextConverter?: (block: TextBlock) => ProsemirrorNode | undefined
    ) {
      function nodeWithDefault(
        block: Block,
        node: ProsemirrorNode | undefined = undefined
      ) {
        if (!node && defaultTextConverter && TextBlock.is(block))
          return defaultTextConverter(block)
        return node
      }

      return function (richText: RichTextContent): ProsemirrorNode | undefined {
        const sections = richText.value.reduce<ProsemirrorNode[]>(
          (acc, block) => {
            const extension = extensions.find((extension) =>
              extension.richTextNodeTypes.includes(block.type)
            )

            const node = getNode({ extension, block, schema })
            const ancestors = acc.slice(0, acc.length - 1)
            const previousNode = last(acc)

            const processedNode: {
              merged: boolean
              updatedNode: ProsemirrorNode | undefined
            } = getProcessedNode({
              node,
              block,
              nodeWithDefault,
              previousNode
            })

            return processedNode.updatedNode
              ? [
                  ...(processedNode.merged ? ancestors : acc),
                  processedNode.updatedNode
                ]
              : acc
          },
          []
        )

        return schema.nodes[componentName]?.create({}, sections)
      }
    },

    toPrismic(document: ProsemirrorNode): RichTextContent {
      const blocks = NodeUtils.children.reduce<readonly Block[]>(
        document,
        (acc, child: ProsemirrorNode) => {
          const extension = Object.values(extensions).find(
            (extension) => child.type.name === extension.name
          )
          const converted = extension?.converter.toPrismic(child)
          if (!converted)
            console.warn(`Cannot convert node of type '${child.type.name}'.`)
          return converted
            ? [
                ...acc,
                ...(converted instanceof Array ? converted : [converted])
              ]
            : acc
        },
        []
      )
      return {
        __TYPE__: "StructuredTextContent",
        value: [...blocks]
      }
    }
  }
}

const EditorDocument = new DocumentExtension(
  componentName,
  component,
  converter
)
// eslint-disable-next-line import/no-default-export
export default EditorDocument

interface getNodeProps {
  extension: NodeExtension | undefined
  block: Block
  schema: Schema
}

function getNode(props: getNodeProps) {
  const { extension, block, schema } = props

  if (!extension) return

  switch (extension.nodeType) {
    case NodeType.Embed:
      if (!EmbedBlock.is(block)) return
      return extension.converter.fromPrismic(schema)(block)

    case NodeType.Text:
      if (!TextBlock.is(block)) return
      return extension.converter.fromPrismic(schema)(block)

    case NodeType.Image:
      if (!ImageBlock.is(block)) return
      return extension.converter.fromPrismic(schema)(block)
  }
}

interface getProcessedNodeProps {
  node: ProsemirrorNode | undefined
  block: Block
  nodeWithDefault: (
    block: Block,
    node?: ProsemirrorNode | undefined
  ) => ProsemirrorNode | undefined

  previousNode: ProsemirrorNode | undefined
}

function getProcessedNode(props: getProcessedNodeProps) {
  const { node, block, nodeWithDefault, previousNode } = props

  if (!node || !previousNode) return { merged: false, updatedNode: node }

  if (
    node.type.name === previousNode.type.name &&
    (node.type.name === BulletList.name || node.type.name === OrderedList.name)
  ) {
    return { merged: true, updatedNode: mergeLists(previousNode, node) }
  }

  return { merged: false, updatedNode: nodeWithDefault(block, node) }
}
